import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthShell, AuthField, AuthError } from "../components/AuthShell";
import { getInviteInfo, formatApiErrorDetail } from "../lib/api";
import { Loader2, Users } from "lucide-react";

export default function InviteAccept() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const token = new URLSearchParams(location.search).get("token") || "";

  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid, object=ok
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    getInviteInfo(token)
      .then((d) => { setInfo(d); setEmail(d.email || ""); })
      .catch(() => setInfo(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim(), token);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (info === undefined) {
    return (
      <AuthShell title="Loading invite…" subtitle="">
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>
      </AuthShell>
    );
  }

  if (info === null) {
    return (
      <AuthShell title="Invite not found" subtitle="This invite is invalid or has already been used.">
        <p className="text-sm text-zinc-400 text-center">
          <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium">Create your own workspace instead →</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Join the team" subtitle={`You've been invited to ${info.workspace_name}.`}>
      <div className="flex items-center gap-2 text-[12px] text-blue-300 bg-blue-600/10 border border-blue-600/25 rounded-md px-3 py-2 mb-4">
        <Users className="h-4 w-4 shrink-0" /> Set a password to join <span className="font-semibold">{info.workspace_name}</span>.
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <AuthError message={error} />
        <AuthField label="Full name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Engineer" data-testid="invite-name" />
        <AuthField label="Work email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
        <AuthField label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="invite-password" />
        <button type="submit" disabled={loading} data-testid="invite-accept-submit" className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-sm font-semibold text-white flex items-center justify-center gap-2 transition-colors">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Join workspace
        </button>
      </form>
      <p className="mt-6 text-sm text-zinc-400 text-center">
        Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">Sign in</Link>
      </p>
    </AuthShell>
  );
}
